import type { EventView, LineNames, ShipmentView } from "@/api/order_journal";
import { groupSscc } from "@/lib/boxes";
import { fmtDateTime, ruNum } from "@/lib/format";

/**
 * Подписи и разбор данных журнала отгрузки — без React, чтобы проверять тестом.
 *
 * Неизвестный код (вид заказа, статус, вид события) показывается КАК ЕСТЬ, а
 * не подменяется «похожей» подписью: новый статус на бэке иначе читался бы
 * как один из старых.
 */

/** Автор и источник количества у сканов/фото до миграции 0166 не писались. */
export const LEGACY_TEXT = "нет данных (до 04.10.2026)";

const KIND_TITLES: Record<string, string> = {
  customer: "Продажа",
  transfer: "Перемещение",
};

export function kindTitle(kind: string): string {
  return KIND_TITLES[kind] ?? kind;
}

const CHECK_STATUS: Record<string, { text: string; tone: Tone }> = {
  open: { text: "Идёт проверка", tone: "warn" },
  completed: { text: "Проверка завершена", tone: "ok" },
  cancelled: { text: "Проверка отменена", tone: "muted" },
};

const PALLET_STATUS: Record<string, { text: string; tone: Tone }> = {
  open: { text: "Собирается", tone: "warn" },
  closed: { text: "Собрана", tone: "ok" },
};

export type Tone = "ok" | "warn" | "muted" | "bad";

export function checkStatus(status: string): { text: string; tone: Tone } {
  return CHECK_STATUS[status] ?? { text: status, tone: "muted" };
}

export function palletStatus(status: string): { text: string; tone: Tone } {
  return PALLET_STATUS[status] ?? { text: status, tone: "muted" };
}

const SCAN_KINDS: Record<string, string> = {
  ean13: "Штрихкод EAN-13",
  gtin: "GTIN",
  datamatrix: "Код маркировки",
  gs1: "GS1",
  sscc: "Короб (SSCC)",
  manual: "Код введён вручную",
};

export function scanKindTitle(kind: string): string {
  return SCAN_KINDS[kind] ?? kind;
}

const QTY_SOURCES: Record<string, string> = {
  scan: "по коду",
  manual_qty: "количество вручную",
  manual_code: "код вручную",
};

/** Источник количества скана; `null` — до 04.10.2026. */
export function qtySourceTitle(source: string | null): string {
  if (source === null) return LEGACY_TEXT;
  return QTY_SOURCES[source] ?? source;
}

export function isManualSource(source: string | null): boolean {
  return source === "manual_qty" || source === "manual_code";
}

export function fmtQty(q: number): string {
  return ruNum.format(q);
}

/** Номенклатура · характеристика · партия одной строкой. */
export function lineTitle(l: Partial<LineNames>): string {
  return [
    [l.nomenclature_article, l.nomenclature_name].filter(Boolean).join(" "),
    l.characteristic_name,
    l.partia_name ? `партия ${l.partia_name}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Кто работал: логины; сканы до 04.10.2026 — «нет данных», а не пусто. */
export function authorsText(authors: string[], legacyScans: number): string {
  const known = authors.join(", ");
  if (legacyScans <= 0) return known || "—";
  return known ? `${known}; часть сканов — ${LEGACY_TEXT}` : LEGACY_TEXT;
}

/** Строка без грузоместа реестра: ключа отгрузки нет — словами, не пусто. */
export const NO_UNIT_TEXT = "без грузоместа";
export const NO_UNIT_HINT =
  "отгрузку к ней не приложить: импорт паллет из 1С не проставил грузоместо — сообщите администратору";

/** Номера паллеты с её этикеток: наш SSCC группами по четыре (`groupSscc`),
 *  у импортированной из 1С — ещё «№ 1С …» со старой этикетки. Без
 *  грузоместа — «без грузоместа», не пусто и не номер 1С из строки. Ни SSCC,
 *  ни архивного номера — подпись грузоместа (`unit_label`: SSCC упаковки и
 *  т. п.), тоже не пусто. */
export function palletNumberText(p: {
  cargo_unit_id: string | null;
  sscc: string | null;
  archive_number: string | null;
  unit_label?: string | null;
}): string {
  if (!p.cargo_unit_id) return NO_UNIT_TEXT;
  const text = [p.sscc ? groupSscc(p.sscc) : "", p.archive_number ? `№ 1С ${p.archive_number}` : ""]
    .filter(Boolean)
    .join(" · ");
  return text || p.unit_label || p.cargo_unit_id;
}

/** Номер паллеты и подпись портала: «4465 0025 4000 0001 7 · Паллета 3». */
export function palletTitle(number: string | null | undefined, label: string | null | undefined): string {
  return [number ?? "", label ?? ""].filter(Boolean).join(" · ") || "Без номера";
}

/** Отгрузка паллеты одной строкой для карточки. */
export function shipmentText(s: ShipmentView): string {
  const parts = [
    `Отгружена ${fmtDateTime(s.scanned_at)}`,
    s.scanned_by ?? "кто — нет данных",
    s.shipment_status === "open" ? "смена погрузки открыта" : "смена погрузки закрыта",
  ];
  if (s.expense_number) {
    parts.push(
      s.expense_posted
        ? `расходная № ${s.expense_number}, проведена${s.expense_posted_at ? ` ${fmtDateTime(s.expense_posted_at)}` : ""}`
        : `расходная № ${s.expense_number}, не проведена`,
    );
  } else {
    parts.push("расходная не создана");
  }
  return parts.join(" · ");
}

// --- события ----------------------------------------------------------------

type Obj = Record<string, unknown>;

function obj(v: unknown): Obj | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : v === null || v === undefined ? "" : String(v);
}

function num(v: unknown): number {
  return typeof v === "number" ? v : Number(v ?? 0);
}

/** Паллета в снимке события: наш SSCC группами (как везде), без него —
 *  номер 1С строки (плановая, архив). */
function palletRef(v: unknown): string {
  const p = obj(v);
  if (!p) return "";
  const sscc = str(p.sscc);
  return palletTitle(sscc ? groupSscc(sscc) : str(p.number) || null, str(p.label) || null);
}

function itemText(v: unknown): string {
  const it = obj(v);
  if (!it) return "";
  const title = lineTitle({
    nomenclature_article: str(it.nomenclature_article),
    nomenclature_name: str(it.nomenclature_name),
    characteristic_name: str(it.characteristic_name),
    partia_name: str(it.partia_name),
  });
  const code = str(it.code);
  return [title, `кол-во ${fmtQty(num(it.qty_base))}`, code ? `код ${code}` : ""]
    .filter(Boolean)
    .join(" · ");
}

function qtyText(v: unknown): string {
  const q = obj(v);
  if (!q) return "";
  return `${fmtQty(num(q.qty_base))} (${qtySourceTitle(q.qty_source === undefined ? null : (q.qty_source as string | null))})`;
}

function compositionText(v: unknown): string {
  const c = obj(v);
  if (!c) return "";
  const lines = Array.isArray(c.lines) ? c.lines.map(obj).filter(Boolean) as Obj[] : [];
  const head = `${palletRef(c.pallet)}: позиций ${num(c.items_count)}, кол-во ${fmtQty(num(c.qty_base))}`;
  const body = lines
    .map((l) => `${lineTitle({
      nomenclature_name: str(l.nomenclature_name),
      characteristic_name: str(l.characteristic_name),
      partia_name: str(l.partia_name),
    })} — ${fmtQty(num(l.qty_base))}`)
    .join("; ");
  return body ? `${head} (${body})` : head;
}

export interface EventText {
  title: string;
  /** Что именно (позиция, паллета). */
  detail: string;
  before?: string;
  after?: string;
  /** Отметка ручного ввода — выделяется на экране. */
  manual?: string;
}

/** Событие проверки заказа → текст ленты. Виды и payload —
 *  `backend/…/order_check/events.py`. */
export function describeEvent(ev: EventView): EventText {
  const p = ev.payload;
  switch (ev.kind) {
    case "item_added": {
      const manual = [
        p.manual_code ? "код введён вручную" : "",
        p.qty_manual ? "количество задано вручную" : "",
      ]
        .filter(Boolean)
        .join(", ");
      return {
        title: "Добавлена позиция",
        detail: [itemText(p.item), palletRef(p.pallet)].filter(Boolean).join(" → "),
        manual: manual || undefined,
      };
    }
    case "qty_changed":
      return {
        title: "Изменено количество",
        detail: [itemText(p.item), palletRef(p.pallet)].filter(Boolean).join(" · "),
        before: qtyText(p.before),
        after: qtyText(p.after),
        manual: "количество задано вручную",
      };
    case "item_deleted":
      return {
        title: "Удалена позиция",
        detail: [
          itemText(p.item),
          palletRef(p.pallet),
          p.scanned_at ? `отсканирована ${fmtDateTime(str(p.scanned_at))}` : "",
          typeof p.scanned_by === "string" ? `сканировал ${p.scanned_by}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "item_moved":
      return {
        title: "Позиция перенесена на другую паллету",
        detail: itemText(p.item),
        before: palletRef(p.from_pallet),
        after: palletRef(p.to_pallet),
      };
    case "photo_added":
      return { title: "Добавлено фото", detail: palletRef(p.pallet) };
    case "photo_deleted":
      return {
        title: "Удалено фото",
        detail: [
          palletRef(p.pallet),
          p.taken_at ? `снято ${fmtDateTime(str(p.taken_at))}` : "",
          typeof p.taken_by === "string" ? `снимал ${p.taken_by}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "pallet_added":
      return {
        title: p.source === "plan" ? "Добавлена паллета по плану из 1С" : "Добавлена паллета",
        detail: palletRef(p.pallet),
      };
    case "pallet_deleted":
      return {
        title: p.source === "plan" ? "Паллета снята планом из 1С" : "Удалена паллета",
        detail: compositionText(p),
      };
    case "session_finished":
      return {
        title: "Проверка завершена",
        detail: p.expense_number ? `расходная № ${str(p.expense_number)}` : "",
      };
    case "session_reopened": {
      const docs = Array.isArray(p.documents_marked_deleted)
        ? (p.documents_marked_deleted as unknown[]).map(str).join(", ")
        : "";
      return {
        title: p.clear_scans
          ? "Проверка возвращена в работу со сбросом сканов"
          : "Проверка возвращена в работу",
        detail: [
          p.expense_number ? `расходная № ${str(p.expense_number)}` : "",
          docs ? `помечены на удаление: ${docs}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    case "scans_cleared": {
      const pallets = Array.isArray(p.pallets) ? p.pallets : [];
      return {
        title: "Сканы заказа очищены",
        detail: pallets.map(compositionText).filter(Boolean).join("; "),
      };
    }
    default:
      return { title: `Событие «${ev.kind}»`, detail: JSON.stringify(p) };
  }
}
